import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import * as _ from 'lodash';
import moment from 'moment';
import { ConfigService } from 'src/config';
import { PassedPosition, VehicleHistory, VehicleHistoryMapper } from '../../shared/models/vehicle-history';
import { VehicleEvent } from '../../shared/models/vehicle-history/vehicle-event';
import { AppStateService } from './app-state.service';
import { VehicleActivity } from '../../shared/models';
import { firstValueFrom, map } from 'rxjs';


@Injectable({
    providedIn: 'root'
})
export class HistoryService {
    private _http = inject(HttpClient);
    private _config = inject(ConfigService);
    private _appState = inject(AppStateService);


    constructor() { }

    public async getVehicleHistory(vehicleInventoryNumber: string,
        startDate: Date,
        endDate: Date,
        startTime: string,
        endTime: string): Promise<VehicleHistory> {

        const positions = await this.getVehiclePositionHistory(vehicleInventoryNumber, startDate, endDate, startTime, endTime);
        const events = await this.getVehicleEventHistory(vehicleInventoryNumber, startDate, endDate, startTime, endTime);

        if (!positions || positions.length <= 1) {
            return;
        }

        const vehicleActivities = await this.getVehicleActivitiesHistory(vehicleInventoryNumber, startDate, endDate, startTime, endTime);

        const history = VehicleHistoryMapper.createVehicleHistory(
            vehicleInventoryNumber, positions, events, vehicleActivities, this._appState.stateSnapshot.punctualityConfig
        );

        return history;
    }

    public async getVehiclePositionHistory(
        vehicleInventoryNumber: string,
        startDate: Date,
        endDate: Date,
        startTime: string,
        endTime: string,
    ): Promise<PassedPosition[]> {
        const startDatetime = this.getDateTimeString(startDate, startTime);
        const endDateTime = this.getDateTimeString(endDate, endTime);

        // eslint-disable-next-line max-len
        const url = `${this._config.mapApiBaseUrl}/v1/vehicles/${vehicleInventoryNumber}/positionHistory?From=${startDatetime}&To=${endDateTime}`;
        return await firstValueFrom(this._http.get(url)
            .pipe(
                map((passedPositions: any[]) => {
                    const pps: PassedPosition[] = [];
                    _.each(passedPositions, passedpos => {
                        pps.push(new PassedPosition({
                            long: passedpos.long,
                            lat: passedpos.lat,
                            // use moment to handle utc offset
                            ts: moment(passedpos.ts).toDate(),
                            bearing: passedpos.bearing
                        }));
                    });
                    return pps;
                })));

    }

    public async getVehicleEventHistory(
        vehicleInventoryNumber: string,
        startDate: Date,
        endDate: Date,
        startTime: string,
        endTime: string): Promise<VehicleEvent[]> {
        const startDatetime = this.getDateTimeString(startDate, startTime);
        const endDateTime = this.getDateTimeString(endDate, endTime);

        // eslint-disable-next-line max-len
        const url = `${this._config.mapApiBaseUrl}/v1/vehicles/${vehicleInventoryNumber}/eventHistory?From=${startDatetime}&To=${endDateTime}`;
        return await firstValueFrom(this._http.get(url)
            .pipe(
                map((events: any[]) => {
                    const output: VehicleEvent[] = [];
                    _.each(events, event => {
                        output.push(new VehicleEvent(event));
                    });
                    return output;
                })));
    }

    public async getVehicleActivitiesHistory(
        vehicleInventoryNumber: string,
        startDate: Date,
        endDate: Date,
        startTime: string,
        endTime: string): Promise<VehicleActivity[]> {
        const startDatetime = this.getDateTimeString(startDate, startTime);
        const endDateTime = this.getDateTimeString(endDate, endTime);

        // eslint-disable-next-line max-len
        const url = `${this._config.mapApiBaseUrl}/v1/vehicleActivities/vehicleHistory/${vehicleInventoryNumber}?From=${startDatetime}&To=${endDateTime}`;
        try {
            return await firstValueFrom(this._http.get(url)
                .pipe(
                    map(result => result?.['vehicleActivities']),
                    map((events: any[]) => {
                        const output: VehicleActivity[] = [];
                        _.each(events, event => {
                            output.push(new VehicleActivity(event));
                        });
                        return output;
                    })));
        } catch (error) {
            return [];
        }
    }

    private getDateTimeString(date: Date, time: string) {
        // use moment to handle utc offset
        return moment(`${date}T${time}`).utc().format();
    }
}
